import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Email enviado');
    } catch (error) {
      toast.error('Error al enviar el email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/auth')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Volver a Iniciar Sesión</span>
        </button>

        {/* Card */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">¿Olvidaste tu contraseña?</h1>
                <p className="text-orange-100 text-sm">Te enviaremos un enlace para restablecerla</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {sent ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                <h2 className="text-lg font-semibold text-slate-900 mb-2">¡Email enviado!</h2>
                <p className="text-slate-600 mb-2">
                  Si existe una cuenta con el email <strong>{email}</strong>, recibirás un enlace para restablecer tu contraseña.
                </p>
                <p className="text-slate-500 text-sm mb-6">
                  Revisa tu bandeja de entrada y carpeta de spam.
                </p>
                <div className="space-y-3">
                  <Button 
                    onClick={() => setSent(false)} 
                    variant="outline"
                    className="w-full rounded-full"
                  >
                    Enviar a otro email
                  </Button>
                  <Button 
                    onClick={() => navigate('/auth')} 
                    className="w-full rounded-full"
                  >
                    Volver a Iniciar Sesión
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-slate-600 text-sm">
                  Introduce tu dirección de email y te enviaremos un enlace para restablecer tu contraseña.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-500 hover:bg-orange-400 rounded-full"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    'Enviar enlace de recuperación'
                  )}
                </Button>

                <p className="text-center text-sm text-slate-500">
                  ¿Recordaste tu contraseña?{' '}
                  <button 
                    type="button"
                    onClick={() => navigate('/auth')}
                    className="text-orange-600 hover:text-orange-700 font-medium"
                  >
                    Iniciar Sesión
                  </button>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
